import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useClientStore } from '../store/client.store.js';
import { useAuthStore } from '../store/auth.store.js';
import { apiGet, apiPost, apiPatch, apiDelete } from '../api/client.js';

function resolveStoresUrl(user, selectedClientId) {
  if (user?.role === 'superadmin') {
    return selectedClientId ? `/clients/${selectedClientId}/stores` : null;
  }
  return '/stores';
}

export const useStores = () => {
  const user = useAuthStore((s) => s.user);
  const { selectedClientId } = useClientStore();
  const url = resolveStoresUrl(user, selectedClientId);
  return useQuery({
    queryKey: ['stores', selectedClientId],
    queryFn: () => apiGet(url),
    enabled: !!url,
  });
};

export const useStore = (id) =>
  useQuery({ queryKey: ['stores', 'detail', id], queryFn: () => apiGet(`/stores/${id}`), enabled: !!id });

export const useCreateStore = () => {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const { selectedClientId } = useClientStore();
  return useMutation({
    mutationFn: (data) => {
      const url = user?.role === 'superadmin' && selectedClientId
        ? `/clients/${selectedClientId}/stores`
        : '/stores';
      return apiPost(url, data);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['stores'] }),
  });
};

export const useUpdateStore = (id) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => apiPatch(`/stores/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['stores'] });
      qc.invalidateQueries({ queryKey: ['stores', 'detail', id] });
    },
  });
};

export const useSyncStore = (id) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost(`/stores/${id}/sync`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['stores'] }),
  });
};

export const useDeleteStore = (id) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiDelete(`/stores/${id}`),
    onSuccess: () => {
      qc.removeQueries({ queryKey: ['stores', 'detail', id] }); // evita refetch 404 antes de navegar
      qc.invalidateQueries({ queryKey: ['stores'] });
    },
  });
};
